/** SheetJS-free preview and import-plan contract. */

export type XlsxRawValue = string | number | boolean | null;

export interface XlsxSheetPreview {
  readonly name: string;
  readonly headers: readonly string[];
  readonly rows: readonly (readonly XlsxRawValue[])[];
  readonly dateSerials?: readonly (readonly (string | null)[])[];
}

export interface XlsxWorkbookPreview { readonly sheets: readonly XlsxSheetPreview[]; }

const TASK_HEADERS = ['Task UID', 'WBS', 'Name', 'Outline Level', 'Start', 'Finish', 'Duration Minutes', 'Work Minutes', 'Percent Complete', 'Fixed Cost', 'Cost', 'Actual Cost', 'Remaining Cost', 'Is Critical', 'Is Milestone', 'Predecessors'] as const;
const RESOURCE_HEADERS = ['Resource UID', 'Name', 'Type', 'Group', 'Max Units', 'Work Minutes', 'Cost', 'Actual Cost', 'Remaining Cost', 'Is Overallocated', 'Is Budget', 'Initials', 'Standard Rate Per Minute', 'Overtime Rate Per Minute', 'Cost Per Use', 'Accrue At', 'Material Label'] as const;
const ASSIGNMENT_HEADERS = ['Task UID', 'Resource UID', 'Units', 'Work Minutes', 'Actual Work Minutes', 'Remaining Work Minutes', 'Overtime Work Minutes', 'Start', 'Finish', 'Rate Table', 'Cost', 'Actual Cost', 'Remaining Cost'] as const;
const DEPENDENCY_HEADERS = ['Predecessor Task UID', 'Successor Task UID', 'Type', 'Lag Minutes', 'Lag Is Percent', 'Lag Is Elapsed'] as const;

export interface TaskImportMapping {
  /** Header name selected by the user; it is the only required mapping. */
  readonly name: string;
  readonly outlineLevel?: string;
  readonly start?: string;
  readonly finish?: string;
  readonly durationMinutes?: string;
  readonly workMinutes?: string;
  readonly percentComplete?: string;
  readonly fixedCost?: string;
}

export interface TaskImportPlanRow {
  readonly sourceRow: number;
  readonly name: string;
  readonly outlineLevel: number;
  readonly start: string | null;
  readonly finish: string | null;
  readonly durationMinutes: number | null;
  readonly workMinutes: number | null;
  readonly percentComplete: number | null;
  readonly fixedCost: number | null;
}

export interface TaskImportDiagnostic {
  readonly sourceRow: number;
  readonly field: keyof TaskImportMapping | 'mapping';
  readonly message: string;
}

export interface TaskImportPlan {
  /** Empty when any diagnostic exists: callers can never partially apply a plan. */
  readonly rows: readonly TaskImportPlanRow[];
  readonly diagnostics: readonly TaskImportDiagnostic[];
}

const ISO_WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/;

function isValidIsoWallClock(value: string): boolean {
  const match = ISO_WALL_CLOCK.exec(value);
  if (match === null) return false;
  const year = Number(match[1]!);
  const month = Number(match[2]!);
  const day = Number(match[3]!);
  const hour = Number(match[4]!);
  const minute = Number(match[5]!);
  const second = Number(match[6]!);
  if (hour > 23 || minute > 59 || second > 59) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function numericValue(value: XlsxRawValue): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function columnIndexes(sheet: XlsxSheetPreview, mapping: TaskImportMapping, diagnostics: TaskImportDiagnostic[]): Partial<Record<keyof TaskImportMapping, number>> {
  const indexes: Partial<Record<keyof TaskImportMapping, number>> = {};
  const mappedColumns = new Set<number>();
  (Object.keys(mapping) as (keyof TaskImportMapping)[]).forEach((field) => {
    const header = mapping[field];
    if (header === undefined) return;
    const matches = sheet.headers.reduce<number[]>((found, candidate, index) => candidate === header ? [...found, index] : found, []);
    if (matches.length !== 1) {
      diagnostics.push({ sourceRow: 1, field: 'mapping', message: `Mapped header "${header}" for ${field} is ${matches.length === 0 ? 'missing' : 'ambiguous'}.` });
      return;
    }
    if (mappedColumns.has(matches[0]!)) {
      diagnostics.push({ sourceRow: 1, field: 'mapping', message: `Header "${header}" cannot map to more than one task field.` });
      return;
    }
    mappedColumns.add(matches[0]!);
    indexes[field] = matches[0];
  });
  return indexes;
}

function mappedValue(row: readonly XlsxRawValue[], index: number | undefined): XlsxRawValue {
  return index === undefined ? null : row[index] ?? null;
}

/**
 * Strictly validates a user-selected sheet/header mapping into a task import plan.
 * It returns no rows when *any* row or mapping diagnostic is present.
 */
export function buildTaskImportPlan(sheet: XlsxSheetPreview, mapping: TaskImportMapping): TaskImportPlan {
  const diagnostics: TaskImportDiagnostic[] = [];
  const indexes = columnIndexes(sheet, mapping, diagnostics);
  if (indexes.name === undefined) return { rows: [], diagnostics };

  const rows: TaskImportPlanRow[] = [];
  let previousOutlineLevel = 1;
  let importedRowCount = 0;
  sheet.rows.forEach((source, index) => {
    const sourceRow = index + 2;
    const blank = source.every((value) => value === null || (typeof value === 'string' && value.trim() === ''));
    if (blank) return;
    const rawName = mappedValue(source, indexes.name);
    const name = typeof rawName === 'string' ? rawName.trim() : '';
    if (name === '') diagnostics.push({ sourceRow, field: 'name', message: 'Name must be a non-empty text value.' });

    const outlineRaw = mappedValue(source, indexes.outlineLevel);
    const outlineLevel = outlineRaw === null ? 1 : numericValue(outlineRaw);
    if (outlineLevel === null || !Number.isInteger(outlineLevel) || outlineLevel < 1) {
      diagnostics.push({ sourceRow, field: 'outlineLevel', message: 'Outline level must be a positive integer.' });
    } else if (importedRowCount === 0 && outlineLevel !== 1) {
      diagnostics.push({ sourceRow, field: 'outlineLevel', message: 'The first nonblank outline level must be 1.' });
    } else if (importedRowCount > 0 && outlineLevel > previousOutlineLevel + 1) {
      diagnostics.push({ sourceRow, field: 'outlineLevel', message: 'Outline level cannot skip more than one hierarchy level.' });
    }
    if (outlineLevel !== null && Number.isInteger(outlineLevel) && outlineLevel >= 1) previousOutlineLevel = outlineLevel;

    const date = (field: 'start' | 'finish'): string | null => {
      const value = mappedValue(source, indexes[field]);
      if (value === null || value === '') return null;
      const serialDate = indexes[field] === undefined ? null : sheet.dateSerials?.[index]?.[indexes[field]!];
      if (serialDate !== null && serialDate !== undefined) return serialDate;
      if (typeof value !== 'string' || !isValidIsoWallClock(value)) {
        diagnostics.push({ sourceRow, field, message: `${field} must be ISO local wall-clock YYYY-MM-DDTHH:mm:ss.` });
        return null;
      }
      return value;
    };
    const boundedNumber = (field: 'durationMinutes' | 'workMinutes' | 'percentComplete' | 'fixedCost', max: number | null): number | null => {
      const value = mappedValue(source, indexes[field]);
      if (value === null || value === '') return null;
      const numeric = numericValue(value);
      if (numeric === null || numeric < 0 || (max !== null && numeric > max)) {
        diagnostics.push({ sourceRow, field, message: `${field} must be a finite ${max === null ? 'non-negative' : `number between 0 and ${max}`}.` });
        return null;
      }
      return numeric;
    };

    const start = date('start');
    const finish = date('finish');
    if (start !== null && finish !== null && finish < start) {
      diagnostics.push({ sourceRow, field: 'finish', message: 'Finish cannot be earlier than start.' });
    }
    rows.push({
      sourceRow,
      name,
      outlineLevel: outlineLevel ?? 1,
      start,
      finish,
      durationMinutes: boundedNumber('durationMinutes', null),
      workMinutes: boundedNumber('workMinutes', null),
      percentComplete: boundedNumber('percentComplete', 100),
      fixedCost: boundedNumber('fixedCost', null),
    });
    importedRowCount += 1;
  });
  return diagnostics.length === 0 ? { rows, diagnostics: [] } : { rows: [], diagnostics };
}

export interface XlsxProjectImportTask extends TaskImportPlanRow {
  readonly uid: number;
}
export interface XlsxProjectImportResource {
  readonly sourceRow: number;
  readonly uid: number;
  readonly name: string;
  readonly type: 'Work' | 'Material' | 'Cost';
  readonly group: string;
  readonly maxUnits: number;
  readonly isBudget: boolean;
  readonly initials: string;
  readonly standardRatePerMinute: number;
  readonly overtimeRatePerMinute: number;
  readonly costPerUse: number;
  readonly accrueAt: 'Start' | 'Prorated' | 'End';
  readonly materialLabel: string | null;
}
export interface XlsxProjectImportAssignment {
  readonly sourceRow: number;
  readonly taskUid: number;
  readonly resourceUid: number;
  readonly units: number;
  readonly workMinutes: number;
  readonly actualWorkMinutes: number;
  readonly remainingWorkMinutes: number;
  readonly overtimeWorkMinutes: number | null;
  readonly start: string;
  readonly finish: string;
  readonly rateTable: 'A' | 'B' | 'C' | 'D' | 'E';
  readonly cost: number;
  readonly actualCost: number;
  readonly remainingCost: number | null;
}
export interface XlsxProjectImportDependency {
  readonly sourceRow: number;
  readonly predecessorTaskUid: number;
  readonly successorTaskUid: number;
  readonly type: 'FS' | 'SS' | 'FF' | 'SF';
  readonly lagMinutes: number;
  readonly lagIsPercent: boolean;
  readonly lagIsElapsed: boolean;
}
export interface XlsxProjectImportDiagnostic {
  readonly sheet: string;
  readonly sourceRow: number;
  readonly field: string;
  readonly message: string;
}
export interface XlsxProjectReplacePlan {
  readonly tasks: readonly XlsxProjectImportTask[];
  readonly resources: readonly XlsxProjectImportResource[];
  readonly assignments: readonly XlsxProjectImportAssignment[];
  readonly dependencies: readonly XlsxProjectImportDependency[];
  readonly diagnostics: readonly XlsxProjectImportDiagnostic[];
}

const RESOURCE_TYPES = new Set(['Work', 'Material', 'Cost']);
const ACCRUAL_TYPES = new Set(['Start', 'Prorated', 'End']);
const RATE_TABLES = new Set(['A', 'B', 'C', 'D', 'E']);
const DEPENDENCY_TYPES = new Set(['FS', 'SS', 'FF', 'SF']);

function strictSheet(
  workbook: XlsxWorkbookPreview,
  name: string,
  headers: readonly string[],
  diagnostics: XlsxProjectImportDiagnostic[],
): XlsxSheetPreview | undefined {
  const matches = workbook.sheets.filter((sheet) => sheet.name === name);
  if (matches.length !== 1) {
    diagnostics.push({ sheet: name, sourceRow: 1, field: 'sheet', message: 'Required sheet is missing or duplicated.' });
    return undefined;
  }
  const sheet = matches[0]!;
  if (sheet.headers.length !== headers.length || sheet.headers.some((header, index) => header !== headers[index])) {
    diagnostics.push({ sheet: name, sourceRow: 1, field: 'headers', message: 'Headers do not match the XLSX v1 schema.' });
  }
  return sheet;
}

function finite(value: XlsxRawValue, min = 0): number | null {
  const parsed = numericValue(value);
  return parsed === null || parsed < min ? null : parsed;
}

function integerUid(value: XlsxRawValue): number | null {
  const parsed = finite(value, 1);
  return parsed === null || !Number.isSafeInteger(parsed) ? null : parsed;
}

function text(value: XlsxRawValue): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function bool(value: XlsxRawValue): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function dateAt(sheet: XlsxSheetPreview, row: number, column: number): string | null {
  const value = sheet.dateSerials?.[row]?.[column] ?? sheet.rows[row]?.[column];
  return typeof value === 'string' && isValidIsoWallClock(value) ? value : null;
}

function isBlankRow(row: readonly XlsxRawValue[]): boolean {
  return row.every((value) => value === null || value === '');
}

function cycleExists(dependencies: readonly XlsxProjectImportDependency[]): boolean {
  const edges = new Map<number, number[]>();
  const indegree = new Map<number, number>();
  for (const dependency of dependencies) {
    let successors = edges.get(dependency.predecessorTaskUid);
    if (successors === undefined) {
      successors = [];
      edges.set(dependency.predecessorTaskUid, successors);
    }
    successors.push(dependency.successorTaskUid);
    indegree.set(dependency.predecessorTaskUid, indegree.get(dependency.predecessorTaskUid) ?? 0);
    indegree.set(dependency.successorTaskUid, (indegree.get(dependency.successorTaskUid) ?? 0) + 1);
  }

  const queue = [...indegree]
    .filter(([, degree]) => degree === 0)
    .map(([uid]) => uid);
  let visited = 0;
  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const uid = queue[cursor]!;
    visited += 1;
    for (const successor of edges.get(uid) ?? []) {
      const nextDegree = (indegree.get(successor) ?? 0) - 1;
      indegree.set(successor, nextDegree);
      if (nextDegree === 0) queue.push(successor);
    }
  }
  return visited !== indegree.size;
}

function parseTasks(
  sheet: XlsxSheetPreview | undefined,
  diagnostics: XlsxProjectImportDiagnostic[],
): { tasks: XlsxProjectImportTask[]; uids: Set<number> } {
  const tasks: XlsxProjectImportTask[] = [];
  const uids = new Set<number>();
  sheet?.rows.forEach((row, index) => {
    if (isBlankRow(row)) return;
    const sourceRow = index + 2;
    const uid = integerUid(row[0] ?? null);
    const name = text(row[2] ?? null);
    const outlineLevel = integerUid(row[3] ?? null);
    const start = dateAt(sheet, index, 4);
    const finish = dateAt(sheet, index, 5);
    const durationMinutes = finite(row[6] ?? null);
    const workMinutes = finite(row[7] ?? null);
    const percentComplete = finite(row[8] ?? null);
    const fixedCost = finite(row[9] ?? null);
    if (
      uid === null || uids.has(uid) || name === null || outlineLevel === null ||
      start === null || finish === null || finish < start || durationMinutes === null ||
      workMinutes === null || percentComplete === null || percentComplete > 100 || fixedCost === null
    ) {
      diagnostics.push({ sheet: 'Tasks', sourceRow, field: 'row', message: 'Invalid or duplicate task UID, required value, range, or date.' });
      return;
    }
    uids.add(uid);
    tasks.push({ sourceRow, uid, name, outlineLevel, start, finish, durationMinutes, workMinutes, percentComplete, fixedCost });
  });

  let previousLevel = 1;
  tasks.forEach((task, index) => {
    if ((index === 0 && task.outlineLevel !== 1) || (index > 0 && task.outlineLevel > previousLevel + 1)) {
      diagnostics.push({ sheet: 'Tasks', sourceRow: task.sourceRow, field: 'Outline Level', message: 'Outline hierarchy is not contiguous.' });
    }
    previousLevel = task.outlineLevel;
  });
  return { tasks, uids };
}

function parseResources(
  sheet: XlsxSheetPreview | undefined,
  diagnostics: XlsxProjectImportDiagnostic[],
): { resources: XlsxProjectImportResource[]; uids: Set<number> } {
  const resources: XlsxProjectImportResource[] = [];
  const uids = new Set<number>();
  sheet?.rows.forEach((row, index) => {
    if (isBlankRow(row)) return;
    const sourceRow = index + 2;
    const uid = integerUid(row[0] ?? null);
    const name = text(row[1] ?? null);
    const type = text(row[2] ?? null);
    const group = typeof row[3] === 'string' ? row[3] : null;
    const maxUnits = finite(row[4] ?? null);
    const isBudget = bool(row[10] ?? null);
    const initials = typeof row[11] === 'string' ? row[11] : null;
    const standardRatePerMinute = finite(row[12] ?? null);
    const overtimeRatePerMinute = finite(row[13] ?? null);
    const costPerUse = finite(row[14] ?? null);
    const accrueAt = text(row[15] ?? null);
    const materialLabel = row[16] === null ? null : typeof row[16] === 'string' ? row[16] : undefined;
    if (
      uid === null || uids.has(uid) || name === null || !RESOURCE_TYPES.has(type ?? '') ||
      group === null || maxUnits === null || isBudget === null || initials === null ||
      standardRatePerMinute === null || overtimeRatePerMinute === null || costPerUse === null ||
      !ACCRUAL_TYPES.has(accrueAt ?? '') || materialLabel === undefined
    ) {
      diagnostics.push({ sheet: 'Resources', sourceRow, field: 'row', message: 'Invalid or duplicate resource value.' });
      return;
    }
    uids.add(uid);
    resources.push({
      sourceRow, uid, name, type: type as XlsxProjectImportResource['type'], group, maxUnits,
      isBudget, initials, standardRatePerMinute, overtimeRatePerMinute, costPerUse,
      accrueAt: accrueAt as XlsxProjectImportResource['accrueAt'], materialLabel,
    });
  });
  return { resources, uids };
}

function parseAssignments(
  sheet: XlsxSheetPreview | undefined,
  taskUids: ReadonlySet<number>,
  resources: readonly XlsxProjectImportResource[],
  resourceUids: ReadonlySet<number>,
  diagnostics: XlsxProjectImportDiagnostic[],
): XlsxProjectImportAssignment[] {
  const assignments: XlsxProjectImportAssignment[] = [];
  const pairs = new Set<string>();
  const budgetResourceUids = new Set(resources.filter((resource) => resource.isBudget).map((resource) => resource.uid));
  sheet?.rows.forEach((row, index) => {
    if (isBlankRow(row)) return;
    const sourceRow = index + 2;
    const taskUid = integerUid(row[0] ?? null);
    const resourceUid = integerUid(row[1] ?? null);
    const units = finite(row[2] ?? null);
    const workMinutes = finite(row[3] ?? null);
    const actualWorkMinutes = finite(row[4] ?? null);
    const remainingWorkMinutes = finite(row[5] ?? null);
    const overtimeWorkMinutes = row[6] === null ? null : finite(row[6] ?? null);
    const start = dateAt(sheet, index, 7);
    const finish = dateAt(sheet, index, 8);
    const rateTable = text(row[9] ?? null);
    const cost = finite(row[10] ?? null);
    const actualCost = finite(row[11] ?? null);
    const remainingCost = row[12] === null ? null : finite(row[12] ?? null);
    const pair = `${String(taskUid)}:${String(resourceUid)}`;
    if (
      taskUid === null || !taskUids.has(taskUid) || resourceUid === null || !resourceUids.has(resourceUid) ||
      pairs.has(pair) || budgetResourceUids.has(resourceUid) || units === null || workMinutes === null ||
      actualWorkMinutes === null || remainingWorkMinutes === null || actualWorkMinutes + remainingWorkMinutes !== workMinutes ||
      (overtimeWorkMinutes !== null && overtimeWorkMinutes > actualWorkMinutes) ||
      (overtimeWorkMinutes === null && row[6] !== null) || start === null || finish === null || finish < start ||
      !RATE_TABLES.has(rateTable ?? '') || cost === null || actualCost === null ||
      (remainingCost === null && row[12] !== null)
    ) {
      diagnostics.push({ sheet: 'Assignments', sourceRow, field: 'row', message: 'Invalid or duplicate assignment reference, work totals, date, rate table, or cost.' });
      return;
    }
    pairs.add(pair);
    assignments.push({
      sourceRow, taskUid, resourceUid, units, workMinutes, actualWorkMinutes, remainingWorkMinutes,
      overtimeWorkMinutes, start, finish, rateTable: rateTable as XlsxProjectImportAssignment['rateTable'],
      cost, actualCost, remainingCost,
    });
  });
  return assignments;
}

function parseDependencies(
  sheet: XlsxSheetPreview | undefined,
  taskUids: ReadonlySet<number>,
  diagnostics: XlsxProjectImportDiagnostic[],
): XlsxProjectImportDependency[] {
  const dependencies: XlsxProjectImportDependency[] = [];
  const tuples = new Set<string>();
  sheet?.rows.forEach((row, index) => {
    if (isBlankRow(row)) return;
    const sourceRow = index + 2;
    const predecessorTaskUid = integerUid(row[0] ?? null);
    const successorTaskUid = integerUid(row[1] ?? null);
    const type = text(row[2] ?? null);
    const lagMinutes = numericValue(row[3] ?? null);
    const lagIsPercent = bool(row[4] ?? null);
    const lagIsElapsed = bool(row[5] ?? null);
    const tuple = `${String(predecessorTaskUid)}:${String(successorTaskUid)}:${String(type)}:${String(lagMinutes)}:${String(lagIsPercent)}:${String(lagIsElapsed)}`;
    if (
      predecessorTaskUid === null || successorTaskUid === null || predecessorTaskUid === successorTaskUid ||
      tuples.has(tuple) || !taskUids.has(predecessorTaskUid) || !taskUids.has(successorTaskUid) ||
      !DEPENDENCY_TYPES.has(type ?? '') || lagMinutes === null || lagIsPercent === null || lagIsElapsed === null
    ) {
      diagnostics.push({ sheet: 'Dependencies', sourceRow, field: 'row', message: 'Invalid or duplicate dependency reference, type, lag, or flag.' });
      return;
    }
    tuples.add(tuple);
    dependencies.push({
      sourceRow, predecessorTaskUid, successorTaskUid,
      type: type as XlsxProjectImportDependency['type'], lagMinutes, lagIsPercent, lagIsElapsed,
    });
  });
  return dependencies;
}

/** Validates the exact four-sheet replacement schema. It never mutates a project. */
export function buildXlsxProjectReplacePlan(workbook: XlsxWorkbookPreview): XlsxProjectReplacePlan {
  const diagnostics: XlsxProjectImportDiagnostic[] = [];
  if (workbook.sheets.length !== 4) {
    diagnostics.push({ sheet: 'Workbook', sourceRow: 1, field: 'sheets', message: 'Exactly Tasks, Resources, Assignments, and Dependencies sheets are required.' });
  }
  const tasksSheet = strictSheet(workbook, 'Tasks', TASK_HEADERS, diagnostics);
  const resourcesSheet = strictSheet(workbook, 'Resources', RESOURCE_HEADERS, diagnostics);
  const assignmentsSheet = strictSheet(workbook, 'Assignments', ASSIGNMENT_HEADERS, diagnostics);
  const dependenciesSheet = strictSheet(workbook, 'Dependencies', DEPENDENCY_HEADERS, diagnostics);
  const { tasks, uids: taskUids } = parseTasks(tasksSheet, diagnostics);
  const { resources, uids: resourceUids } = parseResources(resourcesSheet, diagnostics);
  const assignments = parseAssignments(assignmentsSheet, taskUids, resources, resourceUids, diagnostics);
  const dependencies = parseDependencies(dependenciesSheet, taskUids, diagnostics);
  if (cycleExists(dependencies)) {
    diagnostics.push({ sheet: 'Dependencies', sourceRow: 1, field: 'graph', message: 'Dependency cycle is not supported.' });
  }
  return diagnostics.length === 0 ? { tasks, resources, assignments, dependencies, diagnostics: [] } : { tasks: [], resources: [], assignments: [], dependencies: [], diagnostics };
}
